import { Badge } from '@civilgrid/ui/components';
import { getResearchStatusLabel, getResearchStatusVariant } from '~/common/researchStatus';
import { formatUTCDate } from '~/pages/homeViewV2/utils/dates';

type AgencyDataStatusProps = {
  /** Agency research status (citygrid `research_status` / `Agency.researchStatus`); absent on the legacy Esri citygrid path. */
  researchStatus?: string;
  /** ISO date of the agency's last data refresh (citygrid `last_refresh` / `Agency.lastRefresh`). */
  lastRefresh?: string;
  /** Suffix for the row's data-testids, e.g. the agency name. */
  testIdSuffix?: string;
};

/**
 * "Data Research Status: <chip>  ...  Last Refresh: M/D/YYYY" row at the top of an expanded
 * dataset section (Paving Projects / Paving Moratoria / CIP) on the Intel tab. Both values live
 * on the (jurisdiction, dataset type) agency row, so the row renders per section, not per
 * jurisdiction card. Each side renders only when its value is present/valid; the row renders
 * nothing when neither is. UTC date-only formatting matches the Data Refresh Status page, which
 * shows the same agency fields.
 */
export default function AgencyDataStatus({ researchStatus, lastRefresh, testIdSuffix }: AgencyDataStatusProps) {
  const suffix = testIdSuffix ? `-${testIdSuffix}` : '';
  const hasValidDate = Boolean(lastRefresh) && !Number.isNaN(new Date(lastRefresh as string).getTime());

  if (!researchStatus && !hasValidDate) {
    return null;
  }

  return (
    <div
      className="flex items-center justify-between gap-2 border-b px-2 py-1.5"
      data-testid={`intelligence-agency-data-status${suffix}`}
    >
      {researchStatus ? (
        <div className="flex items-center gap-1" data-testid={`intelligence-research-status${suffix}`}>
          <span className="text-xs font-medium text-grey-70">Data Research Status:</span>
          <Badge color={getResearchStatusVariant(researchStatus)}>{getResearchStatusLabel(researchStatus)}</Badge>
        </div>
      ) : (
        <span />
      )}
      {hasValidDate && (
        <div className="flex items-center gap-1" data-testid={`intelligence-last-refresh${suffix}`}>
          <span className="text-xs font-medium text-grey-70">Last Refresh:</span>
          <span className="text-xs font-medium text-grey-90">{formatUTCDate(lastRefresh)}</span>
        </div>
      )}
    </div>
  );
}
